import getFirstOrderIncentive from "@faire/web-api/api/first-order-incentives/first-order-incentive-data/get";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IFirstOrderIncentiveDiscountData } from "@faire/web-api/indigofair/data/IFirstOrderIncentiveDiscountData";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import { makeObservable } from "@faire/web/common/makeObservable";
import { dangerouslyGetIsSSR } from "@faire/web/common/server-side-rendering/dangerouslyGetIsSSR";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import differenceInDays from "date-fns/differenceInDays";
import differenceInHours from "date-fns/differenceInHours";
import differenceInMinutes from "date-fns/differenceInMinutes";
import differenceInSeconds from "date-fns/differenceInSeconds";
import getTime from "date-fns/getTime";
import { action, computed, observable } from "mobx";

import { setIntervalIfPossible } from "@faire/retailer/lib/async-functions/setIntervalIfPossible";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { MilestonesStore } from "@faire/retailer/stores/domain/MilestonesStore";

/**
 * @deprecated prefer `useFirstOrderIncentiveStore` from 'src/hooks/firstOrderIncentive` instead
 *
 */
export class FirstOrderIncentiveStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(FirstOrderIncentiveStore);

  @observable
  private incentiveData: IFirstOrderIncentiveDiscountData | undefined;

  @observable
  private currentTime: number = getTime(new Date());
  private interval?: number;

  constructor() {
    makeObservable(this);
    this.initialize();
  }

  @action
  private initialize = () => {
    if (!dangerouslyGetIsSSR()) {
      this.interval = setIntervalIfPossible(this.timerCallback, 1000);
    }

    if (!isLoggedInRetailer()) {
      return;
    }

    this.fetchFirstOrderIncentiveData();
  };

  @action
  fetchFirstOrderIncentiveData = async () => {
    getFirstOrderIncentive().then((data) => {
      this.incentiveData = data;
    });
  };

  @action
  private timerCallback = (): void => {
    this.currentTime = getTime(new Date());

    // Cancel the timer if it no longer needs to run.
    if (this.interval && !this.shouldShowAnyFirstOrderIncentive) {
      clearInterval(this.interval);
    }
  };

  @computed
  get timer(): number {
    return this.currentTime;
  }

  @computed
  get shouldShowAnyFirstOrderIncentive(): boolean {
    return (
      this.shouldShowFirstOrderIncentiveModal ||
      this.shouldShowFirstOrderIncentiveBanner ||
      this.shouldShowAddedToBagDeadline ||
      this.shouldShowCheckoutDeadline ||
      this.shouldShowCheckoutNudgeCountdown ||
      this.shouldShowCheckoutSuccess
    );
  }

  @computed
  get shouldShowFirstOrderIncentiveModal(): boolean {
    if (!this.hasIncentive) {
      return false;
    }

    if (this.isLastChanceIncentive) {
      return !MilestonesStore.get().hasMilestoneOccurred(
        "LAST_CHANCE_FIRST_ORDER_INCENTIVE_MODAL_DISMISS"
      );
    }

    return !MilestonesStore.get().hasMilestoneOccurred(
      "FIRST_ORDER_INCENTIVE_MODAL_DISMISS"
    );
  }

  @computed
  get shouldShowFirstOrderIncentiveBanner(): boolean {
    return this.hasIncentive;
  }

  @computed
  private get shouldShowCheckoutNudgeCountdown(): boolean {
    return this.hasIncentive && this.hoursRemaining < 24;
  }

  @computed
  private get shouldShowCheckoutDeadline(): boolean {
    return this.hasIncentive && this.hoursRemaining < 24;
  }

  @computed
  get shouldShowAddedToBagDeadline(): boolean {
    return this.hasIncentive;
  }

  @computed
  private get shouldShowCheckoutSuccess(): boolean {
    return this.hasIncentive;
  }

  @computed
  private get hasIncentive() {
    if (!this.incentiveData) {
      return false;
    }

    const { expires_at, discount_amount, discount_bps } = this.incentiveData;

    return !!(
      (discount_amount || discount_bps) &&
      (expires_at === undefined || this.timer < expires_at)
    );
  }

  @computed
  private get remainingInterval() {
    const expiresAt = this.incentiveData?.expires_at ?? 0;
    const timestamp = Math.min(this.timer, expiresAt);
    return {
      start: timestamp,
      end: expiresAt,
    };
  }

  @computed
  get daysRemaining(): number {
    return differenceInDays(
      this.remainingInterval.end,
      this.remainingInterval.start
    );
  }

  @computed
  get hoursRemaining(): number {
    return differenceInHours(
      this.remainingInterval.end,
      this.remainingInterval.start
    );
  }

  @computed
  get timeRemaining(): string {
    const toTwoDigits = (str: string) => str.padStart(2, "0");
    const totalHours = differenceInHours(
      this.remainingInterval.end,
      this.remainingInterval.start
    );
    const totalMinutes = differenceInMinutes(
      this.remainingInterval.end,
      this.remainingInterval.start
    );
    const totalSeconds = differenceInSeconds(
      this.remainingInterval.end,
      this.remainingInterval.start
    );
    const remainderMinutes = totalMinutes - totalHours * 60;
    const remainderSeconds = totalSeconds - totalMinutes * 60;
    return `${totalHours}:${toTwoDigits(`${remainderMinutes}`)}:${toTwoDigits(
      `${remainderSeconds}`
    )}`;
  }

  @computed
  private get isLastChanceIncentive(): boolean {
    // evergreen FOI depends on incentiveData's expires_at
    // if it is undefined, it is evergreen
    return (
      this.incentiveData?.offer_name ===
        "LAST_CHANCE_FIRST_ORDER_INCENTIVE_V1" ||
      this.incentiveData?.offer_name ===
        "PERSONALIZED_LAST_CHANCE_FIRST_ORDER_INCENTIVE_V1"
    );
  }

  @computed
  get expiresAtDate(): string {
    return intlDate(this.incentiveData?.expires_at ?? 0, "MMM d");
  }

  @computed
  get incentiveAmount(): IFaireMoney | undefined {
    return this.incentiveData?.discount_amount ?? undefined;
  }

  @computed
  get firstOrderIncentiveMinimum(): IFaireMoney | undefined {
    return this.incentiveData?.minimum_amount ?? undefined;
  }

  @computed
  get firstOrderIncentivePercent(): number {
    // Divide by 100 to convert to readable percentage (eg; 50%)
    return (this.incentiveData?.discount_bps || 0) / 100;
  }

  @computed
  get firstOrderIncentiveMaxOfferAmount(): IFaireMoney | undefined {
    return this.incentiveData?.max_offer_amount ?? undefined;
  }
}
